import { useEffect, useState } from "react";
import { toast } from "sonner";
import { opsAPI, MODULE_META, cls } from "./opsApi";
import { BellDot, Trash2, Save, Check } from "lucide-react";

const PRIORITY_LEVELS = ["low","normal","high","critical"];

export default function OpsSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [f, setF] = useState({ module:"", kinds:"*", priority_min:"low", push:true });
  const [saving, setSaving] = useState(false);

  const load = () => opsAPI.subscriptions().then(setSubs);
  useEffect(() => { load(); }, []);

  const upsert = async () => {
    setSaving(true);
    try {
      await opsAPI.upsertSub({
        module: f.module || null,
        kinds:  f.kinds.split(",").map(x=>x.trim()).filter(Boolean) || ["*"],
        priority_min: f.priority_min,
        push: f.push,
      });
      toast.success("Subscription saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this subscription?")) return;
    try { await opsAPI.deleteSub(id); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="ops-subscriptions">
      <div className="text-[13px] text-slate-500">
        Choose which operational events land in your Ops Hub inbox. Empty module = subscribe to all modules for the given priority floor. Without any subscription you only receive <span className="mono">high</span> and <span className="mono">critical</span>.
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="grid grid-cols-5 gap-2">
          <select value={f.module} onChange={e=>setF({...f, module: e.target.value})}
                  data-testid="ops-sub-module" className={cls}>
            <option value="">Any module</option>
            {Object.entries(MODULE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <input value={f.kinds} onChange={e=>setF({...f, kinds: e.target.value})}
                 placeholder="Kinds (e.g. wae.approved,kops.*)" data-testid="ops-sub-kinds" className={cls}/>
          <select value={f.priority_min} onChange={e=>setF({...f, priority_min: e.target.value})}
                  data-testid="ops-sub-priority" className={cls}>
            {PRIORITY_LEVELS.map(p => <option key={p} value={p}>≥ {p}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 text-[13px] px-3 h-10 border border-slate-200 rounded-lg bg-white cursor-pointer">
            <input type="checkbox" checked={f.push} onChange={e=>setF({...f, push: e.target.checked})}
                   data-testid="ops-sub-push"/> In-app push
          </label>
          <button onClick={upsert} disabled={saving} data-testid="ops-sub-save"
                  className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4"/> Save
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {subs.length === 0
          ? <div className="p-10 text-center">
              <BellDot className="w-10 h-10 mx-auto text-slate-300 mb-2"/>
              <div className="text-[13px] text-slate-500">You have no custom subscriptions.</div>
            </div>
          : <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Module</th>
                  <th className="text-left px-4 py-3">Kinds</th>
                  <th className="text-center px-4 py-3">Min Priority</th>
                  <th className="text-center px-4 py-3">Push</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {subs.map(s => (
                  <tr key={s.id} data-testid={`ops-sub-row-${s.id}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 mono text-[12px]">{s.module || "any"}</td>
                    <td className="px-4 py-3 mono text-[11.5px] text-slate-500">
                      {(s.kinds || ["*"]).map(k => <span key={k} className="mr-1 px-1.5 py-0.5 rounded bg-slate-100">{k}</span>)}
                    </td>
                    <td className="px-4 py-3 text-center capitalize">{s.priority_min}</td>
                    <td className="px-4 py-3 text-center">{s.push ? <Check size={16} strokeWidth={2} className="inline text-hocp-mintDeep" /> : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={()=>remove(s.id)} data-testid={`ops-sub-del-${s.id}`}
                              className="h-8 w-8 rounded-lg text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
    </div>
  );
}
